import config from '../config/env.config';

/**
 * Image API Service
 * Handles image upload and analysis
 */

/**
 * Analyze image using Vision API
 * @param {string} base64Image - Base64 encoded image
 * @param {number} maxResults - Maximum number of labels (default: 10)
 * @param {number} minConfidence - Minimum confidence threshold (default: 0.5)
 * @returns {Promise<Object>} - Analysis result with labels and suggested query
 */
export async function analyzeImage(base64Image, maxResults = 10, minConfidence = 0.5) {
    try {
        const url = `${config.apiBaseUrl}/v1/image/analyze`;
        
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-API-Key': config.apiKey,
            },
            body: JSON.stringify({
                image: base64Image,
                maxResults,
                minConfidence
            })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Image analysis error:', error);
        throw error;
    }
}

/**
 * Analyze image using Gemini Pro Vision model
 * @param {string} base64Image - Base64 encoded image
 * @param {number} maxResults - Maximum number of labels (default: 10)
 * @returns {Promise<Object>} - Analysis result with labels and suggested query
 */
export async function analyzeImageWithGemini(base64Image, maxResults = 10) {
    try {
        const url = `${config.apiBaseUrl}/v1/image/analyze-gemini`;
        
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-API-Key': config.apiKey,
            },
            body: JSON.stringify({
                image: base64Image,
                maxResults
            })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Gemini image analysis error:', error);
        throw error;
    }
}

/**
 * Convert file to base64
 * @param {File} file - Image file
 * @returns {Promise<string>} - Base64 encoded string
 */
export function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
        reader.readAsDataURL(file);
    });
}
